import { useEffect, useState } from 'react';
import {
    Layout,
    Menu,
    useGetResourceLabel,
    useLogout,
    useNotify,
    useResourceDefinitions,
    useSidebarState,
} from 'react-admin';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Menu as MuiMenu,
    MenuItem,
    Popover,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation } from 'react-router-dom';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventIcon from '@mui/icons-material/Event';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PetsIcon from '@mui/icons-material/Pets';
import RouteIcon from '@mui/icons-material/Route';
import SaveIcon from '@mui/icons-material/Save';
import StarIcon from '@mui/icons-material/Star';
import StorageIcon from '@mui/icons-material/Storage';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ReviewsIcon from '@mui/icons-material/Reviews';
import logo from '../assets/zoonsk-logo.png';
import { animalApiUrl } from '../apiConfig';
import { getAuthToken } from '../authProvider';

const COLLAPSED_WIDTH = 72;
const EXPANDED_WIDTH = 268;

const compareVersions = (v1, v2) => {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < 3; i += 1) {
        if (parts1[i] > parts2[i]) return 1;
        if (parts1[i] < parts2[i]) return -1;
    }
    return 0;
};

const isValidVersion = (version) => /^\d+\.\d+\.\d+$/.test(version);

const SectionLabel = ({ children, visible }) => visible && (
    <Box className="coreui-menu-section-heading" aria-hidden="true">
        <Typography className="coreui-menu-label">{children}</Typography>
        <Box className="coreui-menu-section-line" />
    </Box>
);

const SidebarItem = ({ menuOpen, currentPath, matchPaths, primaryText, ...props }) => {
    const paths = matchPaths || [props.to];
    const isActive = paths.some((path) => (
        path === '/'
            ? currentPath === '/'
            : currentPath === path || currentPath.startsWith(`${path}/`)
    ));

    return (
        <Tooltip title={menuOpen ? '' : primaryText} placement="right" arrow disableHoverListener={menuOpen}>
            <Box className="coreui-menu-item-wrap">
                <Menu.Item
                    {...props}
                    primaryText={primaryText}
                    className={isActive ? 'coreui-sidebar-item-active' : undefined}
                />
            </Box>
        </Tooltip>
    );
};

const CustomSidebar = ({ isMobile, mobileOpen, onMobileClose }) => {
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useSidebarState();
    const currentWidth = isMobile ? EXPANDED_WIDTH : (isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH);
    const menuOpen = isMobile ? mobileOpen : isExpanded;

    useEffect(() => {
        if (sidebarOpen !== menuOpen) setSidebarOpen(menuOpen);
    }, [menuOpen, setSidebarOpen, sidebarOpen]);

    const closeOnMobile = isMobile ? onMobileClose : undefined;

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? mobileOpen : true}
            onClose={isMobile ? onMobileClose : undefined}
            className="coreui-sidebar"
            ModalProps={{ keepMounted: true }}
            PaperProps={{ className: 'coreui-sidebar-paper' }}
            sx={{
                width: currentWidth,
                flexShrink: isMobile ? undefined : 0,
                transition: 'width 220ms ease',
                '& .MuiDrawer-paper': {
                    width: currentWidth,
                    boxSizing: 'border-box',
                    marginTop: '68px',
                    height: 'calc(100% - 68px)',
                    position: 'fixed',
                    zIndex: 1200,
                    transition: 'width 220ms ease',
                    overflowX: 'hidden',
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                    className="coreui-sidebar-brand"
                    sx={{
                        minHeight: 72,
                        display: 'flex',
                        alignItems: 'center',
                        px: (isExpanded || isMobile) ? 2 : 0,
                        justifyContent: (isExpanded || isMobile) ? 'flex-start' : 'center',
                    }}
                >
                    <Box className="coreui-sidebar-brand-mark"><Box component="img" src={logo} alt="" /></Box>
                    {(isExpanded || isMobile) && (
                        <Box sx={{ minWidth: 0 }}>
                            <Typography className="coreui-sidebar-brand-text">Zoo NSK</Typography>
                            <Typography className="coreui-sidebar-brand-caption">Панель управления</Typography>
                        </Box>
                    )}
                </Box>

                <Box className="coreui-sidebar-scroll" sx={{ p: 1, flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                    <Box component="nav" aria-label="Основная навигация" className={menuOpen ? '' : 'coreui-menu-collapsed'} sx={{ width: '100%' }}>
                        <SectionLabel visible={menuOpen}>Обзор</SectionLabel>
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} to="/" primaryText="Главная" leftIcon={<HomeIcon />} onClick={closeOnMobile} />

                        <SectionLabel visible={menuOpen}>Каталог и навигация</SectionLabel>
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} to="/animals" primaryText="Животные" leftIcon={<PetsIcon />} onClick={closeOnMobile} />
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} to="/popular-animals" primaryText="Популярная подборка" leftIcon={<StarIcon />} onClick={closeOnMobile} />
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} to="/routes" primaryText="Маршруты" leftIcon={<RouteIcon />} onClick={closeOnMobile} />

                        <SectionLabel visible={menuOpen}>Публикации</SectionLabel>
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} to="/news" primaryText="Новости" leftIcon={<NewspaperIcon />} onClick={closeOnMobile} />
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} to="/events" primaryText="Мероприятия" leftIcon={<EventIcon />} onClick={closeOnMobile} />

                        <SectionLabel visible={menuOpen}>Посетители</SectionLabel>
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} matchPaths={['/quests', '/rewards', '/quest-purchases']} to="/quests" primaryText="Квесты и награды" leftIcon={<EmojiEventsIcon />} onClick={closeOnMobile} />
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} to="/external-reviews" primaryText="Отзывы посетителей" leftIcon={<ReviewsIcon />} onClick={closeOnMobile} />
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} to="/guest-users" primaryText="Гости и обращения" leftIcon={<SupportAgentIcon />} onClick={closeOnMobile} />

                        <SectionLabel visible={menuOpen}>Система</SectionLabel>
                        <SidebarItem menuOpen={menuOpen} currentPath={location.pathname} to="/admin-users" primaryText="Администраторы" leftIcon={<AdminPanelSettingsIcon />} onClick={closeOnMobile} />
                    </Box>
                </Box>

                {!isMobile && (
                    <Box className="coreui-sidebar-footer" sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: isExpanded ? 'space-between' : 'center' }}>
                        {isExpanded && <Box><Typography variant="caption" className="coreui-sidebar-footer-title">Рабочее пространство</Typography><Typography variant="caption" className="coreui-sidebar-footer-caption">Zoo NSK</Typography></Box>}
                        <Tooltip title={isExpanded ? 'Свернуть меню' : 'Развернуть меню'} placement="right">
                            <IconButton onClick={() => setIsExpanded((prev) => !prev)} size="small" className="coreui-sidebar-toggler" aria-label={isExpanded ? 'Свернуть меню' : 'Развернуть меню'}>
                                {isExpanded ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};

const CustomAppBar = ({ onToggleTheme, isDarkMode, isMobile, onOpenMobileMenu }) => {
    const location = useLocation();
    const resources = useResourceDefinitions();
    const getResourceLabel = useGetResourceLabel();
    const logout = useLogout();
    const notify = useNotify();
    const [accountAnchorEl, setAccountAnchorEl] = useState(null);
    const [versionAnchorEl, setVersionAnchorEl] = useState(null);
    const [dbVersion, setDbVersion] = useState('');
    const [currentDbVersion, setCurrentDbVersion] = useState('');
    const [versionId, setVersionId] = useState(null);
    const [isLoadingVersion, setIsLoadingVersion] = useState(false);
    const username = localStorage.getItem('username') || sessionStorage.getItem('username') || 'admin';

    useEffect(() => {
        let active = true;
        const fetchCurrentVersion = async () => {
            try {
                const response = await fetch(`${animalApiUrl}/version`);
                if (response.ok && active) {
                    const data = await response.json();
                    setCurrentDbVersion(data.version);
                    setDbVersion(data.version);
                    setVersionId(data.id);
                }
            } catch (error) {
                console.error('Ошибка загрузки версии БД:', error);
            }
        };
        fetchCurrentVersion();
        return () => { active = false; };
    }, []);

    const currentResource = location.pathname.split('/')[1];
    const currentResourceLabel = location.pathname === '/'
        ? 'Главная'
        : (currentResource && resources[currentResource] ? getResourceLabel(currentResource, 2) : 'Панель управления');

    const handleLogout = async () => {
        setAccountAnchorEl(null);
        await logout({}, '/login');
    };

    const handleSaveVersion = async () => {
        if (!isValidVersion(dbVersion)) {
            notify('Неверный формат версии. Используйте формат X.Y.Z (например, 1.2.3)', { type: 'error' });
            return;
        }
        if (currentDbVersion && compareVersions(dbVersion, currentDbVersion) <= 0) {
            notify('Новая версия должна быть больше текущей', { type: 'error' });
            return;
        }

        setIsLoadingVersion(true);
        try {
            const authToken = getAuthToken();
            const headers = { 'Content-Type': 'application/json' };
            if (authToken) headers.Authorization = `Bearer ${authToken}`;

            const response = await fetch(
                versionId
                    ? `${animalApiUrl}/version/${versionId}`
                    : `${animalApiUrl}/version`,
                {
                    method: versionId ? 'PATCH' : 'POST',
                    headers,
                    body: JSON.stringify({ version: dbVersion }),
                },
            );

            if (response.ok) {
                const data = await response.json();
                setCurrentDbVersion(data.version);
                setVersionId(data.id);
                setVersionAnchorEl(null);
                notify('Версия БД успешно обновлена', { type: 'success' });
            } else {
                const error = await response.json();
                notify(error.detail || 'Ошибка при обновлении версии', { type: 'error' });
            }
        } catch (error) {
            notify('Ошибка при обновлении версии БД', { type: 'error' });
            console.error('Ошибка:', error);
        } finally {
            setIsLoadingVersion(false);
        }
    };

    return (
        <Box component="header" className="coreui-topbar" sx={{ position: 'fixed', inset: '0 0 auto 0', minHeight: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 1.25, sm: 2.25 }, zIndex: 1201 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: '1 1 auto' }}>
                {isMobile && (
                    <IconButton onClick={onOpenMobileMenu} color="inherit" className="coreui-topbar-action-btn" aria-label="Открыть меню"><MenuIcon /></IconButton>
                )}
                <Box sx={{ minWidth: 0 }}>
                    <Typography className="coreui-topbar-eyebrow" sx={{ display: { xs: 'none', sm: 'block' } }}>Zoo NSK · Управление контентом</Typography>
                    <Typography variant="body1" className="coreui-topbar-page-title" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentResourceLabel}</Typography>
                </Box>
            </Box>

            <Box className="coreui-topbar-actions" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                <Tooltip title="Версия базы данных">
                    <Button onClick={(event) => setVersionAnchorEl(event.currentTarget)} className="coreui-version-button" startIcon={<StorageIcon fontSize="small" />} color="inherit" aria-haspopup="dialog" aria-expanded={Boolean(versionAnchorEl)}>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>База&nbsp;</Box>{currentDbVersion || '—'}
                    </Button>
                </Tooltip>
                <Popover open={Boolean(versionAnchorEl)} anchorEl={versionAnchorEl} onClose={() => setVersionAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ className: 'coreui-version-popover' }}>
                    <Box sx={{ p: 2.25, width: { xs: 300, sm: 350 } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Версия базы данных</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>Укажите новую версию в формате X.Y.Z. Версия должна быть выше текущей.</Typography>
                        <TextField fullWidth size="small" label="Новая версия" placeholder="1.2.3" value={dbVersion} onChange={(event) => setDbVersion(event.target.value)} disabled={isLoadingVersion} helperText={currentDbVersion ? `Текущая версия: ${currentDbVersion}` : ''} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button size="small" onClick={() => setVersionAnchorEl(null)}>Отмена</Button>
                            <Button variant="contained" size="small" startIcon={isLoadingVersion ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />} onClick={handleSaveVersion} disabled={isLoadingVersion || !dbVersion || dbVersion === currentDbVersion}>Сохранить</Button>
                        </Box>
                    </Box>
                </Popover>

                <Tooltip title={isDarkMode ? 'Светлая тема' : 'Тёмная тема'}>
                    <IconButton onClick={onToggleTheme} color="inherit" className="coreui-topbar-action-btn" aria-label={isDarkMode ? 'Включить светлую тему' : 'Включить тёмную тему'}>
                        {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                    </IconButton>
                </Tooltip>

                <Button onClick={(event) => setAccountAnchorEl(event.currentTarget)} className="coreui-account-button" startIcon={<AccountCircleIcon />} endIcon={<KeyboardArrowDownIcon />} color="inherit" aria-haspopup="menu" aria-expanded={Boolean(accountAnchorEl)} sx={{ minWidth: { xs: 'auto', sm: 112 }, px: { xs: 1, sm: 1.25 }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }, '& .MuiButton-endIcon': { ml: { xs: 0, sm: 1 }, display: { xs: 'none', sm: 'inline-flex' } } }}>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{username}</Box>
                </Button>
                <MuiMenu anchorEl={accountAnchorEl} open={Boolean(accountAnchorEl)} onClose={() => setAccountAnchorEl(null)} className="coreui-account-menu" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                    <Box sx={{ px: 2, py: 1.25 }}><Typography variant="caption" color="text.secondary">Вы вошли как</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{username}</Typography></Box>
                    <Divider />
                    <MenuItem onClick={handleLogout}><LogoutIcon fontSize="small" sx={{ mr: 1.25 }} />Выйти</MenuItem>
                </MuiMenu>
            </Box>
        </Box>
    );
};

export const CustomLayout = ({ onToggleTheme, isDarkMode, ...props }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        if (!isMobile) setMobileOpen(false);
    }, [isMobile]);

    return (
        <Layout
            {...props}
            sidebar={() => <CustomSidebar isMobile={isMobile} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
            appBar={() => <CustomAppBar onToggleTheme={onToggleTheme} isDarkMode={isDarkMode} isMobile={isMobile} onOpenMobileMenu={() => setMobileOpen(true)} />}
        />
    );
};
